import { NotificationStatusEnums, NotificationTypeEnums } from 'config/types/notification'
import useForceUpdate from 'hooks/useForceUpdate'
import { useCallback, useState } from 'react'
import { Trans } from 'react-i18next'
import NotificationService from 'services/NotificationService'
import { useUnreadNotificationAmount } from 'state/notification/hooks'
import styled, { css } from 'styled-components'
import theme from 'theme'
import { colors } from 'theme/colors'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import Heading from 'UIKit/Heading'
import HunnyLazyList from 'UIKit/HunnyLazyList'
import Image from 'UIKit/Image'
import Tab from 'UIKit/Tab'
import Tabs, { StyledIndicator } from 'UIKit/Tabs'
import Text from 'UIKit/Text'
import { RowCenter, RowMiddle } from '../Row'
import NotificationItem, { NotificationItemLoader } from './NotificationItem'

const LazyNotiItem = ({ item }) => <NotificationItem notification={item} />

const NotificationContent: React.FC<{ inPageMode?: boolean }> = ({ inPageMode }) => {
  const [notificationType, setNotificationType] = useState<NotificationTypeEnums>(NotificationTypeEnums.SYSTEM)
  const [statusType, setStatusType] = useState<NotificationStatusEnums>(NotificationStatusEnums.ALL)
  const { dependency, update: forceUpdate } = useForceUpdate()

  const { unreadPrivateAmount, unreadSystemAmount, updateUnreadPrivateAmount, updateUnreadSystemAmount } =
    useUnreadNotificationAmount()

  const request = useCallback(
    (offset: number, limit: number) => {
      const response =
        notificationType === NotificationTypeEnums.PRIVATE
          ? NotificationService.getPrivateNotifications(offset, limit, statusType)
          : NotificationService.getSystemNotifications(offset, limit, statusType)

      return response
    },
    [notificationType, statusType, dependency],
  )

  const handleMarkAllAsRead = async () => {
    if (notificationType === NotificationTypeEnums.SYSTEM) {
      const systemResponse = await NotificationService.markAllAsReadSystemNotification()
      if (systemResponse.code === 'success') {
        forceUpdate()
        updateUnreadSystemAmount(0)
      }
    } else {
      const privateResponse = await NotificationService.markAllAsReadPrivateNotification()
      if (privateResponse.code === 'success') {
        forceUpdate()
        updateUnreadPrivateAmount(0)
      }
    }
  }

  return (
    <Box width="100%" overflowY={inPageMode ? 'unset' : 'hidden'}>
      {!inPageMode && (
        <RowMiddle justifyContent="space-between">
          <Heading scale="xs" fontWeight="bold !important">
            <Trans>Notification</Trans>
          </Heading>
        </RowMiddle>
      )}

      <Box
        width="100%"
        mt={inPageMode ? '0px' : '20px'}
        position={inPageMode ? 'sticky' : 'unset'}
        top={inPageMode ? '48px' : 'unset'}
        background={inPageMode ? `${colors.background} !important` : 'transparent'}
        zIndex={1}
      >
        <Tabs
          value={notificationType}
          onTagChanged={(value) => {
            setNotificationType(value)
            setStatusType(NotificationStatusEnums.ALL)
          }}
          width="100%"
          height={41}
          zIndex={10}
          background={'transparent'}
          borderRadius={theme.radii.extraLarge}
          indicator={
            <StyledIndicator
              width="100% !important"
              height="41px !important"
              borderRadius={theme.radii.small}
              background="rgba(255, 255, 255, 0.08) !important"
              position="relative"
            />
          }
        >
          <StyledTab
            value={NotificationTypeEnums.SYSTEM}
            $isActive={notificationType === NotificationTypeEnums.SYSTEM}
            minWidth="50%"
            height={41}
            position="relative"
            style={{
              userSelect: 'none',
            }}
          >
            <Flex
              position="absolute"
              zIndex={10}
              width="100%"
              height={41}
              alignItems="center"
              justifyContent="center"
              mt={'2px'}
            >
              <Text textAlign={'center'} fontSize="14px" bold>
                <Trans>Platform</Trans>
              </Text>

              <StyledUnreadAmountContainter>
                <Text>{unreadSystemAmount}</Text>
              </StyledUnreadAmountContainter>
            </Flex>
          </StyledTab>
          <StyledTab
            value={NotificationTypeEnums.PRIVATE}
            $isActive={notificationType === NotificationTypeEnums.PRIVATE}
            minWidth="50%"
            height={41}
            position="relative"
            style={{
              userSelect: 'none',
            }}
          >
            <Flex
              position="absolute"
              zIndex={10}
              width="100%"
              height={41}
              alignItems="center"
              justifyContent="center"
              mt={'2px'}
            >
              <Text textAlign={'center'} fontSize="14px" bold>
                <Trans>Personal</Trans>
              </Text>

              <StyledUnreadAmountContainter>
                <Text>{unreadPrivateAmount}</Text>
              </StyledUnreadAmountContainter>
            </Flex>
          </StyledTab>
        </Tabs>
        <RowCenter mt={'20px'}>
          <Box width="100%">
            <Tabs
              value={statusType}
              onTagChanged={(value) => {
                setStatusType(value)
              }}
            >
              <StyledTab
                value={NotificationStatusEnums.ALL}
                $isActive={statusType === NotificationStatusEnums.ALL}
                mb={2}
              >
                <Trans>All</Trans>
              </StyledTab>
              <StyledTab
                value={NotificationStatusEnums.UNREAD}
                $isActive={statusType === NotificationStatusEnums.UNREAD}
                mb={2}
                px="12px"
              >
                <Trans>Unread</Trans>
              </StyledTab>
            </Tabs>
          </Box>
          {(notificationType === NotificationTypeEnums.SYSTEM && unreadSystemAmount > 0) ||
          (notificationType === NotificationTypeEnums.PRIVATE && unreadPrivateAmount > 0) ? (
            <StyledMarkAllAsRead onClick={handleMarkAllAsRead} height="fit-content" pr="0px !important">
              <Text fontSize="12px" color="strokeAlt3" style={{ whiteSpace: 'nowrap' }}>
                <Trans>Mark all as read</Trans>
              </Text>
            </StyledMarkAllAsRead>
          ) : null}
        </RowCenter>
      </Box>

      <StyledListContainer mt="14px">
        <HunnyLazyList
          uniqueKey="id"
          Item={LazyNotiItem}
          Loader={
            <>
              <NotificationItemLoader />
              <NotificationItemLoader />
              <NotificationItemLoader />
            </>
          }
          limit={10}
          request={request}
          noContent={
            <StyledNotificationEmpty flexDirection="column" alignItems="center" mt="24px">
              <Image src="/images/voucher-empty.png" height={48} width={60} />
              <Text mt="12px" fontSize="12px" color="textSubtle">
                <Trans>No notification found</Trans>!
              </Text>
            </StyledNotificationEmpty>
          }
        />
      </StyledListContainer>
    </Box>
  )
}

const StyledNotificationEmpty = styled(Flex)`
  img {
    mix-blend-mode: luminosity;
    opacity: 0.6;
  }
`

const StyledListContainer = styled(Box)`
  max-height: 80%;
  overflow-y: auto;

  ::-webkit-scrollbar-thumb {
    visibility: hidden;
  }

  &:hover {
    ::-webkit-scrollbar-thumb {
      background-color: #2d3137 !important;
      visibility: visible;
    }
  }
`

const StyledUnreadAmountContainter = styled(Flex)`
  background: ${({ theme: { colors } }) => colors.text};
  align-items: center;
  justify-content: center;
  border-radius: 50px;
  min-width: 18px;
  padding-left: 6px;
  padding-right: 6px;
  margin-left: 8px;
  height: 18px;

  ${Text} {
    color: ${({ theme: { colors } }) => colors.inputPlaceHolder} !important;
    font-size: 12px;
    font-weight: bold;
  }
`

const StyledTab = styled(Tab)<{ $isActive: boolean }>`
  font-size: 14px;
  font-weight: ${({ $isActive }) => ($isActive ? 'bold' : 'normal')};
  color: ${({ $isActive, theme: { colors } }) => ($isActive ? colors.text : colors.textSubtle)};
  ${Text} {
    color: ${({ $isActive, theme: { colors } }) => ($isActive ? colors.text : colors.textSubtle)};
  }

  ${({ $isActive }) =>
    !$isActive &&
    css`
      ${StyledUnreadAmountContainter} {
        background: ${({ theme: { colors } }) => colors.backgroundAlt2} !important;
        color: ${({ theme: { colors } }) => colors.textTertiary} !important;
      }
    `}
`

const StyledMarkAllAsRead = styled(Box)`
  cursor: pointer;

  position: relative;
  bottom: 5px;

  & :hover {
    opacity: 0.75;
  }
`

export default NotificationContent
